import { Copy } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Icons } from '@/components/icons';
import { ENV } from '@/constants';
import { useGetOrganisationsSpotlight } from '@/services/organisations/queries';
import { Skeleton } from '@/components/ui/skeleton';
import { showToast } from '@/components/ui/show-toast';

interface SpotlightModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ShortenName = ({ name }: { name?: string }) => {
  if (!name) return null;
  const names = name.split(' ');
  return (
    <>
      {names[0]?.[0]}
      {names[1]?.[0]}
    </>
  );
};

export function SpotlightModal({
  open,
  onOpenChange
}: SpotlightModalProps) {
  const { data: organisation, isLoading } = useGetOrganisationsSpotlight({
    enabled: open, // Only fetch when modal is open
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  if (!organisation && !isLoading) {
    return null;
  }

  const publicProfileUrl = organisation ? `${ENV.PROFILE_URL}/pr/${organisation.slug}` : '';
  const contactPerson = organisation?.contactPerson;
  const displayName = contactPerson?.name || organisation?.name;
  const websiteUrl = organisation?.website?.trim() || '';

  const handleCopy = async (text: string, label: string) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      showToast('success', `${label} copied to clipboard`);
    } catch (err) {
      showToast('error', 'Failed to copy link');
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        className='max-w-2xl p-0 sm:rounded-lg'>
        <DialogHeader className='flex flex-row items-center justify-between border-b px-6 py-4'>
          <DialogTitle className='text-xl font-semibold'>
            Organisation Spotlight
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className='space-y-6 px-6 py-4'>
            <SpotlightSkeleton />
          </div>
        ) : organisation ? (
          <div className='space-y-6 px-6 py-4'>
          {/* Organisation Header */}
          <div className='flex items-start gap-4'>
            {/* Logo */}
            {organisation?.logo && organisation?.logo !== '' ? (
              <div className='relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg border'>
                <img
                  src={organisation.logo}
                  alt={`${organisation.name} logo`}
                  className='h-full w-full object-contain'
                />
              </div>
            ) : (
              <div className='flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-lg bg-muted'>
                <span className='text-lg font-semibold text-muted-foreground'>
                  {organisation?.name?.charAt(0)}
                </span>
              </div>
            )}

            {/* Name and Location */}
            <div className='flex-1 min-w-0'>
              <div className='flex items-center gap-2'>
                <h2 className='text-xl font-bold text-gray-900'>
                  {organisation.name}
                </h2>
                {organisation.emailVerified && (
                  <div className='flex-shrink-0'>
                    <Icons.verified />
                  </div>
                )}
              </div>
              {organisation.headOfficeLocation && (
                <p className='mt-1 text-sm text-gray-500'>
                  {organisation.headOfficeLocation}
                </p>
              )}
            </div>
          </div>

          {/* Description */}
          {organisation.bio && (
            <div className='space-y-2'>
              <h3 className='text-base font-semibold text-gray-900'>
                Description
              </h3>
              <p className='text-sm text-gray-700 leading-relaxed'>
                {organisation.bio.length
                > 300 ? `${organisation.bio.substring(0, 300)}...` : organisation.bio}
              </p>
            </div>
          )}

          {/* Public Profile */}
          <div className='rounded-lg border border-gray-200 bg-gray-50 p-4'>
            <h3 className='mb-2 text-sm font-semibold text-gray-900'>
              Public Profile
            </h3>
            <div className='flex items-center gap-2'>
              <a
                href={publicProfileUrl}
                target='_blank'
                rel='noopener noreferrer'
                className='text-xs text-gray-600 hover:text-primary hover:underline'
                onClick={(e) => e.stopPropagation()}
              >
                {publicProfileUrl}
              </a>
              <button
                onClick={() => handleCopy(publicProfileUrl, 'Public profile link')}
                className='flex-shrink-0 flex items-center justify-center rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
                aria-label='Copy public profile link'
                title='Copy public profile link'
                type='button'
              >
                <Copy className='h-3.5 w-3.5' />
              </button>
            </div>
          </div>

          {/* Website */}
          {websiteUrl && (
            <div className='rounded-lg border border-gray-200 bg-gray-50 p-4'>
              <h3 className='mb-2 text-sm font-semibold text-gray-900'>
                Website
              </h3>
              <div className='flex items-center gap-2'>
                <a
                  href={websiteUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-xs text-gray-600 hover:text-primary hover:underline'
                  onClick={(e) => e.stopPropagation()}
                >
                  {websiteUrl}
                </a>
                <button
                  onClick={() => handleCopy(websiteUrl, 'Website link')}
                  className='flex-shrink-0 flex items-center justify-center rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
                  aria-label='Copy website link'
                  title='Copy website link'
                  type='button'
                >
                  <Copy className='h-3.5 w-3.5' />
                </button>
              </div>
            </div>
          )}

          {/* Primary Contact */}
          {contactPerson && (
            <div className='rounded-lg bg-[#C8EBFE] p-4'>
              <h3 className='mb-3 text-sm font-semibold text-gray-900'>
                Primary contact
              </h3>
              <div className='flex items-start gap-3'>
                {/* Avatar */}
                <div className='relative inline-flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-blue-100 to-blue-200'>
                  <span className='text-sm font-semibold text-blue-700'>
                    <ShortenName name={displayName} />
                  </span>
                </div>

                {/* Contact Details */}
                <div className='flex-1 min-w-0 space-y-1'>
                  {/* Name and LinkedIn */}
                  {displayName && (
                    <div className='flex items-center gap-2'>
                      <h4 className='text-sm font-semibold text-gray-900'>
                        {displayName}
                      </h4>
                      {contactPerson.linkedInUrl && (
                        <a
                          href={contactPerson.linkedInUrl}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='flex-shrink-0 text-blue-600 transition-colors hover:text-blue-700'
                          title='View LinkedIn Profile'
                        >
                          <Icons.linkedIn />
                        </a>
                      )}
                    </div>
                  )}

                  {/* Email and Phone */}
                  <div className='flex flex-wrap items-center gap-3 text-xs text-gray-700'>
                    {contactPerson.email && (
                      <span>{contactPerson.email}</span>
                    )}
                    {contactPerson.email && organisation.phoneNumber && (
                      <span className='text-gray-300'>|</span>
                    )}
                    {organisation.phoneNumber && (
                      <span>{organisation.phoneNumber}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function SpotlightSkeleton() {
  return (
    <>
      {/* Organisation Header Skeleton */}
      <div className='flex items-start gap-4'>
        <Skeleton className='h-16 w-16 rounded-lg' />
        <div className='flex-1 space-y-2'>
          <div className='flex items-center gap-2'>
            <Skeleton className='h-6 w-48' />
            <Skeleton className='h-5 w-5 rounded-full' />
          </div>
          <Skeleton className='h-4 w-32' />
        </div>
      </div>

      {/* Description Skeleton */}
      <div className='space-y-2'>
        <Skeleton className='h-5 w-24' />
        <Skeleton className='h-4 w-full' />
        <Skeleton className='h-4 w-3/4' />
      </div>

      {/* Public Profile Skeleton */}
      <div className='rounded-lg border border-gray-200 bg-gray-50 p-4'>
        <Skeleton className='mb-2 h-4 w-28' />
        <Skeleton className='h-3 w-64' />
      </div>

      {/* Website Skeleton */}
      <div className='rounded-lg border border-gray-200 bg-gray-50 p-4'>
        <Skeleton className='mb-2 h-4 w-20' />
        <Skeleton className='h-3 w-48' />
      </div>

      {/* Primary Contact Skeleton */}
      <div className='rounded-lg bg-[#C8EBFE] p-4'>
        <Skeleton className='mb-3 h-4 w-32' />
        <div className='flex items-start gap-3'>
          <Skeleton className='h-10 w-10 rounded-full' />
          <div className='flex-1 space-y-2'>
            <Skeleton className='h-4 w-40' />
            <Skeleton className='h-3 w-56' />
            <Skeleton className='h-3 w-24' />
          </div>
        </div>
      </div>
    </>
  );
}

